/**
 * Bearer auth and single-retry refresh, shared by axios and by streaming fetch.
 *
 * `lib/api/client.ts` has owned this logic since the beginning, but only for
 * axios. The assistant streams with `fetch` (a POST with a body and an
 * `Authorization` header — `EventSource` can do neither, which is why the other
 * stream needs single-use tickets), so the refresh step lives here and both
 * callers share it. Concurrent 401s collapse into one refresh: refresh tokens
 * are single-use, so two parallel refreshes sign the user out.
 */

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

export interface TokenStore {
  getAccess(): string | null;
  getRefresh(): string | null;
  set(access: string, refresh?: string): void;
  clear(): void;
}

export const browserTokens: TokenStore = {
  getAccess: () => (typeof window === 'undefined' ? null : localStorage.getItem('access_token')),
  getRefresh: () => (typeof window === 'undefined' ? null : localStorage.getItem('refresh_token')),
  set: (access, refresh) => {
    localStorage.setItem('access_token', access);
    if (refresh) localStorage.setItem('refresh_token', refresh);
  },
  clear: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  },
};

export interface AuthOptions {
  store?: TokenStore;
  fetchImpl?: typeof fetch;
  /** Called once when the session is gone for good. */
  onSignedOut?: () => void;
}

let inFlight: Promise<string | null> | null = null;

/** Test seam: drop the shared in-flight refresh between specs. */
export function __resetRefreshForTests(): void {
  inFlight = null;
}

export async function refreshAccessToken(options: AuthOptions = {}): Promise<string | null> {
  const store = options.store ?? browserTokens;
  const fetchImpl = options.fetchImpl ?? fetch;

  if (inFlight) return inFlight;

  // `attempt` is assigned to `inFlight` before `.finally` is attached below.
  // If this body never awaits (e.g. the no-refresh-token branch), it runs to
  // completion synchronously — including a `finally` inside this IIFE would
  // fire *before* `inFlight = attempt` executes, and that assignment would
  // then clobber the reset, leaving `inFlight` stuck forever. Chaining
  // `.finally` on the already-created promise avoids that: the callback is
  // always scheduled as a microtask, so it can only run after `inFlight =
  // attempt` has completed.
  const attempt = (async () => {
    try {
      const refresh = store.getRefresh();
      if (!refresh) {
        store.clear();
        return null;
      }
      const response = await fetchImpl(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!response.ok) {
        store.clear();
        return null;
      }
      const data = (await response.json()) as { access_token?: string; refresh_token?: string };
      if (!data.access_token) {
        store.clear();
        return null;
      }
      store.set(data.access_token, data.refresh_token);
      return data.access_token;
    } catch {
      try {
        store.clear();
      } catch {
        // `clear()` can itself throw — e.g. `localStorage` in Safari private
        // mode/ITP, or a sandboxed/partitioned iframe — and a caller-supplied
        // `TokenStore` might too. Swallow it: we're already on the failure
        // path (treat as signed-out), and letting a second throw escape here
        // would reject `attempt` below with no one awaiting it directly.
      }
      return null;
    }
  })();

  inFlight = attempt;
  // `.finally()` returns a new promise derived from `attempt`; nothing else
  // holds a reference to it. If `attempt` ever rejected, that derived promise
  // would surface as an unhandled rejection independent of whatever handler
  // `attempt` itself gets from its caller — attach one so it can't escape.
  void attempt
    .finally(() => {
      if (inFlight === attempt) inFlight = null;
    })
    .catch(() => {});

  return attempt;
}

/**
 * `fetch` against the API with the bearer token attached, retried once after a
 * refresh if the first attempt is a 401.
 *
 * `init.body` must be a string (or absent): the retry reuses it, and a stream
 * body cannot be read twice. Every caller in `lib/api/assistant.ts` passes JSON.
 */
export async function authFetch(
  path: string,
  init: RequestInit = {},
  options: AuthOptions = {},
): Promise<Response> {
  const store = options.store ?? browserTokens;
  const fetchImpl = options.fetchImpl ?? fetch;
  const url = path.startsWith('http') ? path : `${API_URL}${path}`;

  const send = (token: string | null): Promise<Response> => {
    const headers = new Headers(init.headers);
    if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json');
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return fetchImpl(url, { ...init, headers });
  };

  const first = await send(store.getAccess());
  if (first.status !== 401) return first;

  const refreshed = await refreshAccessToken({ store, fetchImpl });
  if (!refreshed) {
    options.onSignedOut?.();
    return first;
  }
  return send(refreshed);
}
